import {
  useCallback,
  useState,
  useEffect,
  useMemo,
  Fragment,
  Dispatch,
  SetStateAction,
  createElement,
  useRef,
  ReactNode,
  DragEvent,
  DragEventHandler,
  ChangeEventHandler,
} from "react";
import JSZip from "jszip";
import { v4 as uuid } from "uuid";
import messages from "messages";
import { throttle } from "lodash";
// components
import { ReactComponent as OutlineChevronRight } from "assets/svg/outline-chevron-right-16.svg";
import ActionsBar from "./components/actions-bar";
import GridView from "./components/grid-view";
import ListView from "./components/list-view/ListView";
import BottomBar from "./components/bottom-bar";
import TreeWidget from "./components/tree-widget";
import NoAttachment from "./components/no-attachment";
import UploadZone from "../../../upload-zone";
import ErrorTag from "components/error-tag";
import FolderPath from "./components/folder-path";
// styles
import styles from "./styles.module.scss";
// utils
import downloadBlob from "utils/downloadBlob";
import updateOrCreateItemInArray from "./utils/updateOrCreateItemInArray";
// hooks
import useDrag2Select from "./hooks/useDrag2Select";
import {
  CreateDirectoryFunc,
  UploadFileGenericFunc,
  DeleteEntryFunc,
  BatchEntryOpsFunc,
} from "contexts/orchid-drive/components/orchidDriveProvider";
import useMovedToTag from "./hooks/useMovedToTag";
// constants
import { TASK_STATUS } from "./constants";
// types
import {
  FileEntry,
  FileUploadEntry,
  OrchidDriveDirectory,
  OrchidDriveFile,
} from "contexts/orchid-drive/types";
import type { TDataInView } from "../hooks/useOrchidDriveDataInView";
// contexts
import { ActionsMenuContextProvider } from "./components/actions-menu/contexts/actions-menu-context/ActionsMenuContext";
// utils
import renameDuplicate from "./utils/renameDuplicate";
import downloadOrchidDriveFiles, {
  ABORT_NAME,
} from "./utils/downloadOrchidDriveFilesBFS";
import { getValidFilePath } from "./utils/getValidFilePath";
import getSumInMap from "./utils/getSumInMap";
import removeItemInArray from "./utils/removeItemInArray";
import { AmplitudePageName } from "utils/amplitude";

type Props = {
  PermanentFolder?: ReactNode;
  patient?: PatientChartContact;
  patientUserId?: string;
  dataInView: TDataInView;
  createDirectory: CreateDirectoryFunc;
  uploadFile: UploadFileGenericFunc;
  deleteEntry?: DeleteEntryFunc;
  batchEntryOps: BatchEntryOpsFunc;
  folderPaths: OrchidDriveDirectory[];
  setFolderPaths: Dispatch<SetStateAction<OrchidDriveDirectory[]>>;
  /**
   * drive data source, e.g. "member-drive", "patient-charts"...
   */
  storageDrive: StorageDrive;
  /**
   * if set to true, hide actions bar (including grid / list toggle button)
   */
  hideActionsBar?: boolean;
  amplitudePageName: AmplitudePageName;
  setOrderByField?: Dispatch<SetStateAction<string>>;
  setOrderDescending?: Dispatch<SetStateAction<boolean>>;
};

export type WatchTask = {
  id: string;
  name: string;
  isFolder?: boolean;
  size?: number;
};

export type Task = {
  id: string;
  state?: keyof typeof TASK_STATUS;
  receivedLength?: Record<string, number>;
  contentLength?: number;
  percent?: number;
  busy?: boolean;
  scheduleCancel?: boolean;
  // completeMessage?: string;
  /**
   * upload target folder path name
   */
  uploadFolderName?: string;
  /**
   * upload / download file name
   */
  fileName?: string;
  /**
   * is folder type
   */
  isFolder?: boolean;
  /**
   * zip blob ready to be downloaded, if any
   */
  blob?: Blob;
  /**
   * size in kb
   */
  size?: number;
  entryIds?: string[];
};

export type SelectedItem = { type: string | undefined; guid: string } & (
  | OrchidDriveFile
  | OrchidDriveDirectory
);

export type AttachmentsViewProps = {
  deleteEntry?: DeleteEntryFunc;
  /**
   * whether have data in view
   */
  dataEntryLength: number;
  dataInView: TDataInView;
  setFolderPaths: Dispatch<SetStateAction<OrchidDriveDirectory[]>>;
  selected?: SelectedItem[];
  setSelected?: Dispatch<SetStateAction<SelectedItem[]>>;
  handleSelect?: (arg: SelectedItem) => void;
  registerSelectableRef?: (
    key: string,
    value: {
      element: HTMLDivElement | null;
      value: SelectedItem;
    },
  ) => void;
  hideActionsMenu?: boolean;
  onDrop: DragEventHandler<HTMLDivElement>;
  storageDrive: StorageDrive;
  batchEntryOps?: BatchEntryOpsFunc;
  onError?: (arg: string | Error) => void;
  onDownload?: (arg: SelectedItem[]) => void;
  isSelectedDragging: boolean;
  setIsSelectedDragging: (arg: boolean) => void;
  analyticsPageName: AmplitudePageName;
  onMoveEnd?: ({
    selected,
    originFolder,
    targetFolder,
    success,
  }: {
    selected: SelectedItem[];
    originFolder?:
      | {
          name: string;
        }
      | undefined;
    targetFolder?:
      | {
          name: string;
        }
      | undefined;
    success: boolean;
  }) => void;
  setOrderByField?: Dispatch<SetStateAction<string>>;
  setOrderDescending?: Dispatch<SetStateAction<boolean>>;
};

const Attachments = ({
  PermanentFolder,
  patient,
  patientUserId,
  dataInView,
  createDirectory,
  uploadFile,
  deleteEntry,
  batchEntryOps,
  folderPaths,
  setFolderPaths,
  storageDrive,
  hideActionsBar,
  amplitudePageName,
  setOrderByField,
  setOrderDescending,
}: Props) => {
  const [isGrid, setIsGrid] = useState(false);

  // on note attachment drive, only allow to show in list
  useEffect(() => {
    if (storageDrive === "note-attachment") {
      setIsGrid(false);
    }
  }, [storageDrive]);
  // error
  const [showErrorTag, setShowErrorTag] = useState(false);
  const [error, setError] = useState<string | Error>();

  const onError = useCallback<NonNullable<AttachmentsViewProps["onError"]>>(
    (arg) => {
      setError(arg);
      setShowErrorTag(true);
    },
    [],
  );
  // select for download
  // might include guid as "**/*.pdf" or folder path as "**/**";
  const [selected, setSelected] = useState<SelectedItem[]>([]);
  useEffect(() => {
    // should clear selected if go up or go down a level
    setSelected((s) => s.slice(s.length));
  }, [folderPaths]);

  // id for current selection, and used as future task id
  const [selectedId, setSelectedId] = useState("");
  // on selection change, should reset current selected id to ""
  useEffect(() => setSelectedId(""), [selected]);
  // use to control the checkbox download busy state
  const [tasks, setTasks] = useState<Task[]>([]);

  const handleDownload = useCallback(
    async (downloadItems: SelectedItem[]) => {
      // sanity check
      if (!downloadItems.length) {
        return;
      }

      const id = uuid();

      setSelectedId(id);
      const abortController = new AbortController();
      try {
        const entryIds = downloadItems.map((el) => el.guid);
        let fileName = `Orchid_drive_download_${Date.now()}.zip`;
        let contentLength: number = 0;
        // if download a single folder, use the folder name as zip name
        if (downloadItems.length === 1) {
          fileName =
            downloadItems[0].name +
            (downloadItems[0].type === "folder" ? ".zip" : "");
        } else if (patient) {
          fileName = `Orchid_${getValidFilePath(
            [patient?.last_name, patient?.first_name]
              .filter((e) => e)
              .join("_"),
          )}.zip`;
        }

        setTasks((s) =>
          updateOrCreateItemInArray(s, {
            id,
            state: TASK_STATUS.downloading,
            percent: 0,
            receivedLength: {},
            contentLength,
            busy: true,
            fileName,
            entryIds,
          }),
        );

        const downloaded = await downloadOrchidDriveFiles({
          entries: downloadItems,
          storageDrive,
          patient,
          fetchOptions: { signal: abortController.signal },
          onDownloadCallback: ({ receivedLength, contentLength }) => {
            setTasks((s) => {
              const item = s.find((el) => el.id === id);
              if (!item) {
                abortController.abort();
                return s;
              }
              if (item.scheduleCancel) {
                abortController.abort();
                return removeItemInArray(s, {
                  id,
                });
              }

              const nextReceivedLength = {
                ...item.receivedLength,
                [id]: receivedLength,
              };

              const nextContentLength = item.contentLength || contentLength;

              return updateOrCreateItemInArray(s, {
                id,
                receivedLength: nextReceivedLength,
                contentLength: nextContentLength,
                percent:
                  (getSumInMap(nextReceivedLength) / (nextContentLength || 1)) *
                  100,
              });
            });
          },
        });

        // direct download on single file
        if (
          downloaded.files.length === 1 &&
          downloaded.directories.length === 0
        ) {
          return setTasks((s) => {
            const taskId = s.findIndex((el) => el.id === id);
            if (taskId < 0) {
              return s;
            }

            if (s[taskId].scheduleCancel) {
              return removeItemInArray(s, {
                id,
              });
            }
            downloadBlob(downloaded.files[0].blob, downloaded.files[0].name);

            return removeItemInArray(s, { id });
          });
        }

        const zip = new JSZip();

        setTasks((s) =>
          updateOrCreateItemInArray(
            s,
            {
              id,
              fileName,
              state: TASK_STATUS.zipping,
              percent: 0,
            },
            true,
          ),
        );

        // produce zip structure
        const files = [...downloaded.files];
        const directories = [...downloaded.directories];
        while (directories.length) {
          const dir = directories.pop();
          if (!dir) {
            break;
          }
          zip.folder(dir.relativePath);
        }
        while (files.length) {
          const file = files.pop();
          if (!file) {
            break;
          }

          zip.file(
            (!file.relativePath ? "" : `${file.relativePath}/`) + file.name,
            file.blob,
            {
              binary: true,
              createFolders: true,
            },
          );
        }

        // when everything has been downloaded, we can trigger the dl
        const blob = await zip.generateAsync(
          { type: "blob" },
          throttle(
            (metaData) =>
              setTasks((s) =>
                updateOrCreateItemInArray(
                  s,
                  {
                    id,
                    percent: metaData.percent,
                  },
                  true,
                ),
              ),
            // arbitrary ms to update
            500,
          ),
        );

        setTasks((s) => {
          const taskId = s.findIndex((el) => el.id === id);
          if (taskId < 0) {
            abortController.abort();
            return s;
          }

          if (s[taskId].scheduleCancel) {
            abortController.abort();
            return removeItemInArray(s, {
              id,
            });
          }
          downloadBlob(blob, s[taskId].fileName || fileName);
          return removeItemInArray(s, { id });
        });
        return;
      } catch (e: any) {
        if (e.name === ABORT_NAME) {
          return;
        }
        setTasks((s) =>
          updateOrCreateItemInArray(
            s,
            {
              id,
              state: TASK_STATUS.failed,
              busy: false,
            },
            true,
          ),
        );
        setError(e.toString());
        console.error(e);
      }

      return () => {
        abortController.abort();
      };
    },
    [patient, storageDrive],
  );

  // select attachment (or folder)
  const handleSelect = useCallback(
    (arg: SelectedItem) =>
      setSelected((s) => {
        const id = s.findIndex(
          (el) => el.guid === arg.guid && el.type === arg.type,
        );
        if (id < 0) {
          // append
          return [...s, arg];
        }
        return [...s.slice(0, id), ...s.slice(id + 1)];
      }),
    [],
  );

  /**
   * use to query the offset for auto scrolling
   */
  const viewContainerRef = useRef<HTMLDivElement | null>(null);

  const refs = useRef<
    Record<
      string,
      {
        element: HTMLElement | null;
        value: SelectedItem;
      }
    >
  >({});
  useEffect(() => {
    refs.current = {};
  }, [dataInView, isGrid]);

  const { DragElement, isSelectedDragging, setIsSelectedDragging } =
    useDrag2Select(
      viewContainerRef.current,
      selected,
      refs.current,
      setSelected,
      storageDrive,
    );

  // upload
  const [watchTasks, setWatchTasks] = useState<WatchTask[]>([]);

  const currDirId = useMemo(
    () => folderPaths[folderPaths.length - 1]?.directoryId || null,
    [folderPaths],
  );

  const handleUpload = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      if (!event.target?.files?.length) {
        return;
      }

      // long path first
      const filesSortedByPathLength = [...event.target.files].sort((a, b) =>
        /**
         * use cautiously
         * @see https://caniuse.com/?search=webkitEntries
         */

        a.webkitRelativePath.split("/").length <
        b.webkitRelativePath.split("/").length
          ? 1
          : -1,
      );

      const dirPathMap: Map<string, { id: string | null; name: string }> =
        new Map();
      // set root folder id
      dirPathMap.set("", { id: currDirId, name: "" });

      const uploadFilesByRootDir = new Map<
        { id: string | null; name: string } | null,
        FileUploadEntry[]
      >();

      function resolveParentDirectory(
        folderPaths: string[],
        pathMap: typeof dirPathMap,
      ): Promise<{ id: string | null; name: string }> {
        return new Promise(async (resolve) => {
          const dirName = folderPaths[folderPaths.length - 1];

          const folderPathsString = folderPaths.join("/");
          if (!pathMap.has(folderPathsString)) {
            const nextDirName =
              folderPaths.length > 1
                ? dirName
                : renameDuplicate(dirName, dataInView.foldersInView);

            const dir = await createDirectory({
              storageDrive,
              patientUserId,
              name: nextDirName,
              parentDirectoryId: (
                await resolveParentDirectory(folderPaths.slice(0, -1), pathMap)
              ).id,
            });

            const res = {
              id: dir.directoryId,
              name: nextDirName,
            };
            pathMap.set(folderPathsString, res);
            resolve(res);
          } else {
            resolve(pathMap.get(folderPathsString)!);
          }
        });
      }

      try {
        while (filesSortedByPathLength.length) {
          const currEntry = filesSortedByPathLength.pop();

          if (currEntry) {
            const nextName = currEntry.webkitRelativePath
              ? currEntry.name
              : renameDuplicate(currEntry.name, dataInView.filesInView);

            if (!currEntry.webkitRelativePath) {
              // after creating folders, bundle all files in one upload task
              const uploadTask = await uploadFile({
                storageDrive,
                patientUserId,
                files: [
                  {
                    file: new FileEntry(currEntry, { name: nextName }),
                    baseDirectoryId: currDirId,
                  },
                ],
              });

              if (uploadTask) {
                setWatchTasks((s) => [
                  ...s,
                  {
                    id: uploadTask.uploadTaskId,
                    name: nextName,
                    size: currEntry.size,
                    isFolder: false,
                  },
                ]);
              } else {
                throw new Error("upload task is not created");
              }
            } else if (currEntry?.webkitRelativePath) {
              const paths = currEntry.webkitRelativePath
                .split("/")
                .slice(0, -1);
              const parentDirectory = await resolveParentDirectory(
                paths,
                dirPathMap,
              );

              const rootDirectory = await resolveParentDirectory(
                paths.slice(0, 1),
                dirPathMap,
              );

              if (!uploadFilesByRootDir.has(rootDirectory)) {
                uploadFilesByRootDir.set(rootDirectory, []);
              }

              uploadFilesByRootDir.get(rootDirectory)!.push({
                file: new FileEntry(currEntry, { name: nextName }),
                baseDirectoryId: parentDirectory.id,
              });
            }
          }
        }

        uploadFilesByRootDir.forEach(async (files, rootDir) => {
          // after creating folders, bundle all files in one upload task
          const uploadTask = await uploadFile({
            storageDrive,
            patientUserId,
            files,
          });

          if (uploadTask) {
            setWatchTasks((s) => [
              ...s,
              {
                id: uploadTask.uploadTaskId,
                name: rootDir
                  ? rootDir.name
                  : files.map((el) => el.file.name).join(","),
                size: files.reduce(
                  (prev, curr) => prev + (curr.file.size ?? 0),
                  0,
                ),
                isFolder: files.length > 1,
              },
            ]);
          } else {
            throw new Error("upload task is not created");
          }
        });
      } catch (error) {
        console.error(error);
        onError(messages.oops);
      } finally {
        event.target.value = "";
      }
    },
    [
      createDirectory,
      currDirId,
      dataInView.filesInView,
      dataInView.foldersInView,
      onError,
      patientUserId,
      storageDrive,
      uploadFile,
    ],
  );

  const handleDrop = useCallback(
    async (event: DragEvent<HTMLDivElement>) => {
      if (!event.dataTransfer?.items?.length) {
        return;
      }

      const dataTransferItemsByDirId: [
        // root dir (relative to current dir)
        { id: string | null; name: string } | null,
        // current dir
        { id: string | null; name: string },
        // entries
        (FileSystemEntry | null)[],
      ][] = [
        [
          null,
          { id: currDirId, name: "" },
          [
            ...event.dataTransfer.items,
            /**
             * use cautiously
             * @see https://caniuse.com/?search=webkitGetAsEntry
             */
          ].map((el) => el.webkitGetAsEntry()),
        ],
      ];

      const uploadFilesByRootDir = new Map<
        { id: string | null; name: string } | null,
        FileUploadEntry[]
      >();

      try {
        while (dataTransferItemsByDirId.length) {
          const currDir = dataTransferItemsByDirId.shift();

          if (currDir) {
            const [rootDir, nextCurrDir, dataTransferItems] = currDir;

            for (const currEntry of dataTransferItems) {
              if (currEntry?.isFile) {
                const file: File = await new Promise((resolve) => {
                  (currEntry as any).file(resolve);
                });
                const nextName =
                  nextCurrDir.id !== currDirId
                    ? file.name
                    : renameDuplicate(file.name, dataInView.filesInView);

                if (rootDir === null) {
                  // after creating folders, bundle all files in one upload task
                  const uploadTask = await uploadFile({
                    storageDrive,
                    patientUserId,
                    files: [
                      {
                        file: new FileEntry(file, { name: nextName }),
                        baseDirectoryId: nextCurrDir.id,
                      },
                    ],
                  });

                  if (uploadTask) {
                    setWatchTasks((s) => [
                      ...s,
                      {
                        id: uploadTask.uploadTaskId,
                        name: nextName,
                        size: file.size,
                        isFolder: false,
                      },
                    ]);
                  } else {
                    throw new Error("upload task is not created");
                  }
                } else {
                  if (!uploadFilesByRootDir.has(rootDir)) {
                    uploadFilesByRootDir.set(rootDir, []);
                  }
                  uploadFilesByRootDir.get(rootDir)?.push({
                    file: new FileEntry(file, { name: nextName }),
                    baseDirectoryId: nextCurrDir.id,
                  });
                }
              } else if (currEntry?.isDirectory) {
                const nextName =
                  nextCurrDir.id !== currDirId
                    ? currEntry.name
                    : renameDuplicate(currEntry.name, dataInView.foldersInView);

                const dir = await createDirectory({
                  storageDrive,
                  patientUserId,
                  name: nextName,
                  parentDirectoryId: nextCurrDir.id,
                });

                const dirReader = (
                  currEntry as FileSystemDirectoryEntry
                ).createReader();
                const entries: FileSystemEntry[] = await new Promise(
                  (resolve) => dirReader.readEntries(resolve),
                );

                dataTransferItemsByDirId.push([
                  rootDir || { id: dir.directoryId, name: nextName },
                  {
                    id: dir.directoryId,
                    name: nextName,
                  },
                  entries,
                ]);
              }
            }
          }
        }

        uploadFilesByRootDir.forEach(async (files, rootDir) => {
          // after creating folders, bundle all files in one upload task
          const uploadTask = await uploadFile({
            storageDrive,
            patientUserId,
            files,
          });

          if (uploadTask) {
            setWatchTasks((s) => [
              ...s,
              {
                id: uploadTask.uploadTaskId,
                name: rootDir
                  ? rootDir.name
                  : files.map((el) => el.file.name).join(","),
                size: files.reduce(
                  (prev, curr) => prev + (curr.file.size ?? 0),
                  0,
                ),
                isFolder: files.length > 1,
              },
            ]);
          } else {
            throw new Error("upload task is not created");
          }
        });
      } catch (error) {
        console.error(error);
        onError(messages.oops);
      }
    },
    [
      createDirectory,
      currDirId,
      dataInView.filesInView,
      dataInView.foldersInView,
      onError,
      patientUserId,
      storageDrive,
      uploadFile,
    ],
  );

  const dataEntryLength = useMemo(
    () =>
      Object.values(dataInView).reduce((prev, curr) => prev + curr.length, 0),
    [dataInView],
  );

  const { MovedToTag, onMoveEnd } = useMovedToTag();

  const onMove = useCallback(
    async ({
      originFolder,
      targetFolder,
    }: {
      originFolder: Pick<OrchidDriveDirectory, "name">;
      targetFolder: { name: string; directoryId: string | null };
    }) => {
      if (
        !batchEntryOps ||
        !isSelectedDragging ||
        storageDrive === "note-attachment"
      ) {
        return;
      }

      if ("directoryId" in targetFolder) {
        if (selected.some((el) => el.guid === targetFolder.directoryId)) {
          return;
        }
      }

      try {
        await batchEntryOps({
          entryMoves: selected.map((el) => ({
            name: el.name,
            entryId: el.guid,
            parentDirectoryId: targetFolder.directoryId,
          })),
          entryDeletes: [],
        });

        onMoveEnd({
          selected,
          originFolder,
          targetFolder,
          success: true,
        });

        return setSelected([]);
      } catch (error) {
        console.error(error);
        return onMoveEnd({
          selected,
          originFolder,
          targetFolder,
          success: false,
        });
      }
    },
    [batchEntryOps, isSelectedDragging, onMoveEnd, selected, storageDrive],
  );

  return (
    <div className={styles["attachments"]}>
      {/* drag2select area */}
      {dataEntryLength > 0 && DragElement}
      {MovedToTag}
      <ErrorTag
        show={showErrorTag}
        onHide={() => setShowErrorTag(false)}
        duration={5000}
        errorMsg={error?.toString()}
      />
      {/* fixed header part */}
      <div className={styles["attachments__breadcrumb"]}>
        {!folderPaths.length ? (
          <span>{messages.attachments}</span>
        ) : (
          <FolderPath
            children={messages.attachments}
            onClick={() => setFolderPaths((s) => s.slice(s.length))}
            onMouseUp={
              !isSelectedDragging
                ? undefined
                : () =>
                    onMove({
                      originFolder: folderPaths[folderPaths.length - 1],
                      targetFolder: {
                        name: messages.attachments,
                        directoryId: null,
                      },
                    })
            }
          />
        )}
        {folderPaths.map((el, i) => (
          <Fragment key={el.directoryId}>
            <OutlineChevronRight
              className={styles["attachments__breadcrumb-connector"]}
            />
            {i === folderPaths.length - 1 ? (
              <span className={styles["attachments__folder-path"]}>
                {el.name}
              </span>
            ) : (
              <FolderPath
                className={styles["attachments__folder-path"]}
                children={el.name}
                onClick={() => setFolderPaths((s) => s.slice(0, i + 1))}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={
                  !isSelectedDragging
                    ? undefined
                    : () =>
                        onMove({
                          originFolder: folderPaths[folderPaths.length - 1],
                          targetFolder: el,
                        })
                }
              />
            )}
          </Fragment>
        ))}
      </div>
      <div
        ref={viewContainerRef}
        className={styles["attachments__content"]}
        draggable={false}
        onDragStart={() => false}
      >
        {PermanentFolder}
        {/* actions bar */}
        {!hideActionsBar && (
          <ActionsBar
            storageDrive={storageDrive}
            isGrid={isGrid}
            setIsGrid={(v) => setIsGrid(v)}
            createDirectory={createDirectory}
            onUpload={handleUpload}
            patientUserId={patientUserId}
            currDirId={folderPaths[folderPaths.length - 1]?.directoryId || null}
            analyticsPageName={amplitudePageName}
          />
        )}
        {/* grid / list view */}
        {!dataEntryLength &&
          (storageDrive === "note-attachment" ? (
            <NoAttachment />
          ) : (
            <UploadZone
              className={styles["attachments__upload-zone"]}
              onChange={handleUpload}
              onDrop={handleDrop}
              storageDrive={storageDrive}
            />
          ))}
        <ActionsMenuContextProvider>
          <TreeWidget
            patient={patient}
            storageDrive={storageDrive}
            folderPaths={folderPaths}
            // onMove={onMove}
            onMoveEnd={(arg) => {
              if (arg.success) {
                setSelected([]);
              }
              onMoveEnd(arg);
            }}
          >
            {dataEntryLength > 0 &&
              createElement(isGrid ? GridView : ListView, {
                analyticsPageName: amplitudePageName,
                deleteEntry,
                selected,
                setSelected,
                handleSelect,
                dataInView,
                dataEntryLength,
                setFolderPaths,
                storageDrive,
                registerSelectableRef: (key, value) =>
                  (refs.current[key] = value),
                onDrop: handleDrop,
                batchEntryOps,
                onError,
                onDownload: handleDownload,
                isSelectedDragging,
                setIsSelectedDragging,
                onMoveEnd: (arg) => {
                  if (arg.success) {
                    setSelected([]);
                  }
                  return onMoveEnd({
                    ...arg,
                    originFolder: folderPaths[folderPaths.length - 1],
                  });
                },
                setOrderByField,
                setOrderDescending,
              })}

            {/* bottom bar */}
            <BottomBar
              selected={selected}
              onCancelSelected={() => setSelected(() => [])}
              onDownload={() => handleDownload(selected)}
              batchEntryOps={batchEntryOps}
              onCancelDownload={(task) =>
                setTasks((s) => removeItemInArray(s, { id: task.id }))
              }
              // cancel all tasks
              onCancelAllDownloads={() => setTasks([])}
              selectedId={selectedId}
              tasks={tasks}
              analyticsPageName={amplitudePageName}
              {...{ storageDrive, watchTasks, setWatchTasks, onError }}
            />
          </TreeWidget>
        </ActionsMenuContextProvider>
      </div>
    </div>
  );
};

export default Attachments;
